import ApiService from './api.service';

/**
 * This gets a paginated index list from the server
 * @param params
 * @returns {Promise<T>}
 */
export function index(params = {}) {
  return ApiService.query('users', params)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get a single record back
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function show(id, params = {}) {
  return ApiService.get('users', id, params)
    .then((response) => {
      return response.data;
    });
}

/**
 * Create a record
 * @param params
 * @returns {Promise<T>}
 */
export function create(params) {
  return ApiService.post('users', params, false)
    .then((response) => {
      return response.data;
    });
}

/**
 * Update existing record
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function update(id, params) {
  return ApiService.update('users', id, params)
    .then((response) => {
      return response.data;
    });
}

/**
 * Destroy record
 * @param id
 * @returns {Promise<AxiosResponse<any> | void>}
 */
export function destroy(id) {
  return ApiService.destroy(`users/${id}`);
}

/**
 * Get all groups associated to user
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function groups(id, v2OrganizationId = null, params = {}) {
  const modifiedParams = {...{v2_organization_id: v2OrganizationId}, ...params};
  return ApiService.query(`users/${id}/groups`, modifiedParams)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get all users current activities
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function activities(id, v2OrganizationId, params = {}) {
  const modifiedParams = {...{v2_organization_id: v2OrganizationId}, ...params};
  return ApiService.query(`users/${id}/activities`, modifiedParams)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get all submissions for all activities for user
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function submissions(id, v2OrganizationId, params = {}) {
  const modifiedParams = {...{v2_organization_id: v2OrganizationId}, ...params};
  return ApiService.query(`users/${id}/submissions`, modifiedParams)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get Users default organization, (which org member record that is marked as default: true OR some complicated logic)
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function defaultOrganization(id, params = {}) {
  return ApiService.query(`users/${id}/default_organization`, params)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get all scoped alerts to the user, scoped to organization
 * @param id
 * @param v2OrganizationId
 * @param params
 * @returns {Promise<T>}
 */
export function alerts(id, v2OrganizationId = null, params = {}) {
  const modifiedParams = {...{v2_organization_id: v2OrganizationId}, ...params};
  return ApiService.query(`users/${id}/alerts`, modifiedParams)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get all scoped notification alerts to the user, scoped to organization
 * @param id
 * @param v2OrganizationId
 * @param params
 * @returns {Promise<T>}
 */
export function alertNotifications(id, v2OrganizationId = null, params = {}) {
  const modifiedParams = {...{v2_organization_id: v2OrganizationId}, ...params};
  return ApiService.query(`users/${id}/alert_notifications`, modifiedParams)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get the last healthstatus for user scoped to organization
 * @param id
 * @param v2OrganizationId
 * @param params
 * @returns {Promise<T>}
 */
export function healthStatus(id, v2OrganizationId = null, params = {}) {
  const modifiedParams = {...{v2_organization_id: v2OrganizationId}, ...params};
  return ApiService.query(`users/${id}/health_status`, modifiedParams)
    .then((response) => {
      return response.data;
    });
}

/**
 * Manually trigger an xAPI sync request for an organization for this user
 * @param id
 * @param v2OrganizationId
 * @param params
 * @returns {Promise<T>}
 */
export function xApiSync(id, v2OrganizationId = null, params = {}) {
  const modifiedParams = {...{v2_organization_id: v2OrganizationId}, ...params};
  return ApiService.query(`users/${id}/xapi_sync`, modifiedParams)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get all organizations for this user
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function organizations(id, params = {}) {
  return ApiService.query(`users/${id}/organizations`, params)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get all syndicates for this user
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function syndicates(id, params = {}) {
  return ApiService.query(`users/${id}/syndicates`, params)
    .then((response) => {
      return response.data;
    });
}

/**
 * Get all memberships for this user
 * @param id
 * @param params
 * @returns {Promise<T>}
 */
export function members(id, params = {}) {
  return ApiService.query(`users/${id}/members`, params)
    .then((response) => {
      return response.data;
    });
}

/**
 * So this seems a bit funny, especially in the naming scheme but it basically just sends a request to record that we
 * intend to signout. It wont actually sign out. That's in the auth service
 * @param id
 * @returns {Promise<AxiosResponse<any> | void>}
 */
export function signOutSignal(id, sessionUuid = null) {
  const params = {
    session_uuid: sessionUuid
  };
  return ApiService.query(`users/${id}/sign_out`, params).then(() => {
    ApiService.destroy('users/sign_out');
  });
}

export default {
  index,
  show,
  create,
  update,
  destroy,
  groups,
  activities,
  submissions,
  defaultOrganization,
  alerts,
  healthStatus,
  xApiSync,
  organizations,
  members,
  signOutSignal,
  syndicates,
  alertNotifications,
};
